import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CircularProgress, Container, Snackbar, Alert } from '@mui/material';
import { useAuthSession } from './AuthSessionContext';
import axiosInstance from './axiosInstance';
import GridView from './GridView';
import ReviewPage from './ReviewPage';
import { formatDate, revertDateFormat } from './dateUtils';
import './ReviewDocsPage.css';

const escapeRegex = (value = '') => value.split('').map((char) => {
  if ("\\^$*+?.()|{}[]".includes(char)) {
    return `\\${char}`;
  }
  return char;
}).join('');

const LETTER_RANGE_NO_MATCH_VALUE = '__letter_range_no_match__';

const ReviewDocsPage = () => {
  const { roles: userRoles } = useAuthSession();
  const isReaderOnly =
    userRoles.includes('reader') &&
    !userRoles.some((role) => role === 'admin' || role === 'reviewer');
  const defaultSelectedDocTypes = ['invoice', 'credit_note'];
  const defaultSelectedDocStatuses = ['in_progress', 'in_review'];
  const pageLimit = 10;

  const [pageLoading, setPageLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [filteredDataList, setFilteredDataList] = useState([]);
  const [detailedData, setDetailedData] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [tempEditableData, setTempEditableData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [nominalCodes, setNominalCodes] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [docStatuses, setDocStatuses] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [docRejectReasons, setDocRejectReasons] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRepairLocation, setSelectedRepairLocation] = useState('');
  const [selectedNominalCode, setSelectedNominalCode] = useState('');
  const [supplierStartChar, setSupplierStartChar] = useState('');
  const [supplierEndChar, setSupplierEndChar] = useState('');
  const [sortCriteria, setSortCriteria] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isEdited, setIsEdited] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalNetValue, setTotalNetValue] = useState(0);
  const [allowedDocStatuses] = useState(['in_progress', 'in_review', 'rejected', 'approved']);
  const [selectedDocStatuses, setSelectedDocStatuses] = useState(defaultSelectedDocStatuses);
  const [selectedDocTypes, setSelectedDocTypes] = useState(defaultSelectedDocTypes);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '', open: false });
  const [exporting, setExporting] = useState(false);
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');
  const [uploadedStartDate, setUploadedStartDate] = useState('');
  const [uploadedEndDate, setUploadedEndDate] = useState('');
  const [createdStartDate, setCreatedStartDate] = useState('');
  const [createdEndDate, setCreatedEndDate] = useState('');
  const [jobNumberFilter, setJobNumberFilter] = useState('');
  const [discountOperator, setDiscountOperator] = useState('<');
  const [discountValue, setDiscountValue] = useState('');
  const [includeInNetTotalsFilter, setIncludeInNetTotalsFilter] = useState('any');
  const [creditRequestedFilter, setCreditRequestedFilter] = useState('any');
  const [currentDocumentGlobalIndex, setCurrentDocumentGlobalIndex] = useState(-1);
  const [currentDataOffset, setCurrentDataOffset] = useState(0);

  const transformWrapperRef = useRef(null);
  const docStatusesRef = useRef(docStatuses);
  const docTypesRef = useRef(docTypes);
  const suppressFetchRef = useRef(false);
  const nextInvoiceStartDateRef = useRef(invoiceStartDate);
  const nextUploadedStartDateRef = useRef(uploadedStartDate);
  const nextCreatedStartDateRef = useRef(createdStartDate);
  const initialPageLoadRef = useRef(true);
  const pendingFilterSyncRef = useRef(false);
  const isFetchingPageRef = useRef(false);
  const suppressAutoNavigationRef = useRef(false);
  const activeDocumentIdRef = useRef(null);
  const latestDocumentRequestRef = useRef(0);

  useEffect(() => {
    docStatusesRef.current = docStatuses;
  }, [docStatuses]);

  useEffect(() => {
    docTypesRef.current = docTypes;
  }, [docTypes]);

  useEffect(() => {
    nextInvoiceStartDateRef.current = invoiceStartDate;
  }, [invoiceStartDate]);

  useEffect(() => {
    nextUploadedStartDateRef.current = uploadedStartDate;
  }, [uploadedStartDate]);

  useEffect(() => {
    nextCreatedStartDateRef.current = createdStartDate;
  }, [createdStartDate]);

  const handlePreviousImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
    zoomOut();
  }, []);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex < detailedData.images.length - 1 ? prevIndex + 1 : prevIndex));
    zoomOut();
  }, [detailedData]);

  const parseDisplayDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map((part) => Number(part));
    if ([day, month, year].some((num) => Number.isNaN(num))) return null;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const parseDateForSort = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }
    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts.map((part) => Number(part));
        if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
          const parsed = new Date(year, month - 1, day);
          if (!Number.isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }
      const fallback = new Date(value);
      if (!Number.isNaN(fallback.getTime())) {
        return fallback;
      }
    }
    return null;
  };

  const normaliseLetter = (value) => {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    return trimmed[0].toUpperCase();
  };

  const getSupplierIdsInLetterRange = (startLetter, endLetter, supplierList = []) => {
    const start = normaliseLetter(startLetter);
    const end = normaliseLetter(endLetter);
    if (!start || !end || start > end) return [];

    const list = Array.isArray(supplierList) ? supplierList : [];
    return list
      .map((supplier) => {
        if (!supplier) return { id: '', firstLetter: '' };
        const displayName = (supplier.name || supplier.supplier_id || '').trim();
        const firstLetter = displayName ? displayName[0].toUpperCase() : '';
        return { id: supplier.supplier_id, firstLetter };
      })
      .filter(({ id, firstLetter }) => Boolean(id) && Boolean(firstLetter) && firstLetter >= start && firstLetter <= end)
      .map(({ id }) => id);
  };

  const normaliseStringForSort = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase();
  };

  const sortedFilteredDataList = useMemo(() => {
    if (!Array.isArray(filteredDataList)) return [];

    const listToSort = [...filteredDataList];

    if (sortCriteria && sortOrder) {
      const statusMap = Array.isArray(docStatuses)
        ? docStatuses.reduce((acc, status) => {
          acc[status.doc_status_id] = status.name;
          return acc;
        }, {})
        : {};

      const typeMap = Array.isArray(docTypes)
        ? docTypes.reduce((acc, type) => {
          acc[type.doc_type_id] = type.name;
          return acc;
        }, {})
        : {};

      const locationMap = Array.isArray(locations)
        ? locations.reduce((acc, location) => {
          acc[location.location_id] = location.name;
          return acc;
        }, {})
        : {};

      const supplierMap = Array.isArray(suppliers)
        ? suppliers.reduce((acc, supplier) => {
          acc[supplier.supplier_id] = supplier.name;
          return acc;
        }, {})
        : {};

      listToSort.sort((a, b) => {
        let valueA = a[sortCriteria];
        let valueB = b[sortCriteria];

        if (sortCriteria === 'total') {
          valueA = Number(a.total) || 0;
          valueB = Number(b.total) || 0;
        } else if (['invoice_date', 'uploaded_on', 'created_on'].includes(sortCriteria)) {
          const dateA = parseDateForSort(a[sortCriteria]);
          const dateB = parseDateForSort(b[sortCriteria]);
          valueA = dateA ? dateA.getTime() : 0;
          valueB = dateB ? dateB.getTime() : 0;
        } else if (sortCriteria === 'doc_type_id') {
          valueA = normaliseStringForSort(typeMap[a.doc_type_id]);
          valueB = normaliseStringForSort(typeMap[b.doc_type_id]);
        } else if (sortCriteria === 'doc_status_id') {
          valueA = normaliseStringForSort(statusMap[a.doc_status_id]);
          valueB = normaliseStringForSort(statusMap[b.doc_status_id]);
        } else if (sortCriteria === 'job_number') {
          valueA = normaliseStringForSort(a.job_number);
          valueB = normaliseStringForSort(b.job_number);
        } else if (sortCriteria === 'location_id') {
          valueA = normaliseStringForSort(locationMap[a.location_id]);
          valueB = normaliseStringForSort(locationMap[b.location_id]);
        } else if (sortCriteria === 'repair_location_id') {
          valueA = normaliseStringForSort(locationMap[a.repair_location_id]);
          valueB = normaliseStringForSort(locationMap[b.repair_location_id]);
        } else if (sortCriteria === 'supplier_id') {
          valueA = normaliseStringForSort(supplierMap[a.supplier_id]);
          valueB = normaliseStringForSort(supplierMap[b.supplier_id]);
        } else {
          valueA = normaliseStringForSort(valueA);
          valueB = normaliseStringForSort(valueB);
        }

        if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return listToSort.map((doc, index) => ({
      ...doc,
      __sequenceIndex: currentDataOffset + index,
    }));
  }, [
    filteredDataList,
    sortCriteria,
    sortOrder,
    docStatuses,
    docTypes,
    locations,
    suppliers,
    currentDataOffset,
  ]);

  const parseIsoDate = (isoDate) => {
    if (!isoDate) return null;
    const [year, month, day] = isoDate.split('-').map((part) => Number(part));
    if ([year, month, day].some((num) => Number.isNaN(num))) return null;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isValidIsoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

  const addDaysToIsoDate = (isoDate, days) => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-').map((part) => Number(part));
    if ([year, month, day].some((num) => Number.isNaN(num))) return '';
    const adjusted = new Date(Date.UTC(year, month - 1, day + days));
    const adjustedYear = adjusted.getUTCFullYear();
    const adjustedMonth = String(adjusted.getUTCMonth() + 1).padStart(2, '0');
    const adjustedDay = String(adjusted.getUTCDate()).padStart(2, '0');
    return `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
  };

  const normaliseOffset = (rawOffset) => {
    if (!rawOffset) return '+00:00';
    const trimmed = rawOffset.trim();
    if (/^[+-]\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const simpleMatch = trimmed.match(/^([+-]?)(\d{1,2})$/);
    if (simpleMatch) {
      const sign = simpleMatch[1] === '-' ? '-' : '+';
      const hours = simpleMatch[2].padStart(2, '0');
      return `${sign}${hours}:00`;
    }
    if (/^[+-]\d$/.test(trimmed)) {
      const sign = trimmed.startsWith('-') ? '-' : '+';
      const hours = trimmed.replace(/[+-]/, '').padStart(2, '0');
      return `${sign}${hours}:00`;
    }
    if (/^[+-]\d{1,2}:\d{2}$/.test(trimmed)) {
      const sign = trimmed.startsWith('-') ? '-' : '+';
      const [h, m] = trimmed.slice(1).split(':');
      return `${sign}${h.padStart(2, '0')}:${m}`;
    }
    return '+00:00';
  };

  const getLondonOffsetSuffix = (isoDate) => {
    try {
      const [year, month, day] = isoDate.split('-').map((part) => Number(part));
      if ([year, month, day].some((num) => Number.isNaN(num))) return '+00:00';
      const sampleDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        timeZoneName: 'shortOffset',
      }).formatToParts(sampleDate);
      const tzPart = parts.find((part) => part.type === 'timeZoneName');
      if (tzPart?.value) {
        const rawOffset = tzPart.value.replace('GMT', '').replace('UTC', '');
        return normaliseOffset(rawOffset);
      }
    } catch (error) {
      console.error('Error determining London offset:', error);
    }
    return '+00:00';
  };

  const buildLondonMidnightIso = (isoDate, offsetDays = 0) => {
    if (!isoDate) return '';
    const adjustedIso = offsetDays ? addDaysToIsoDate(isoDate, offsetDays) : isoDate;
    if (!adjustedIso) return '';
    const offsetSuffix = getLondonOffsetSuffix(adjustedIso);
    return `${adjustedIso}T00:00:00${offsetSuffix}`;
  };

  const buildServerFilter = () => {
    const filter = {};
    if (selectedSupplier) filter.supplier_id = selectedSupplier;
    if (selectedLocation) filter.location_id = selectedLocation;
    if (selectedRepairLocation) filter.repair_location_id = selectedRepairLocation;
    if (selectedNominalCode) filter.nominal_code_id = selectedNominalCode;
    const rawRangeStart = normaliseLetter(supplierStartChar);
    const rawRangeEnd = normaliseLetter(supplierEndChar);
    const hasRangeFilter = Boolean(rawRangeStart) || Boolean(rawRangeEnd);
    if (hasRangeFilter && Array.isArray(suppliers) && suppliers.length > 0) {
      const rangeStartLetter = rawRangeStart || 'A';
      const rangeEndLetter = rawRangeEnd || 'Z';
      const supplierIds = getSupplierIdsInLetterRange(rangeStartLetter, rangeEndLetter, suppliers);
      if (supplierIds.length === 0) {
        filter.supplier_id = LETTER_RANGE_NO_MATCH_VALUE;
      } else if (typeof filter.supplier_id === 'string') {
        if (!supplierIds.includes(filter.supplier_id)) {
          filter.supplier_id = LETTER_RANGE_NO_MATCH_VALUE;
        }
      } else if (filter.supplier_id && Array.isArray(filter.supplier_id.$in)) {
        const intersect = filter.supplier_id.$in.filter((id) => supplierIds.includes(id));
        filter.supplier_id = intersect.length > 0 ? { $in: intersect } : LETTER_RANGE_NO_MATCH_VALUE;
      } else if (!filter.supplier_id) {
        filter.supplier_id = { $in: supplierIds };
      }
    }
    if (docSearchQuery) {
      filter.invoice_number = { $regex: escapeRegex(docSearchQuery), $options: 'i' };
    }
    if (jobNumberFilter) {
      const trimmedJob = jobNumberFilter.trim();
      if (trimmedJob) {
        filter.job_number = { $regex: `^${escapeRegex(trimmedJob)}`, $options: 'i' };
      }
    }

    if (selectedDocStatuses.length > 0) {
      filter.doc_status_id = { $in: selectedDocStatuses };
    } else if (docStatusesRef.current.length > 0) {
      filter.doc_status_id = {
        $in: docStatusesRef.current
          .filter((d) => allowedDocStatuses.includes(d.doc_status_id))
          .map((d) => d.doc_status_id),
      };
    }

    if (selectedDocTypes.length > 0) {
      filter.doc_type_id = { $in: selectedDocTypes };
    } else if (docTypesRef.current.length > 0) {
      filter.doc_type_id = {
        $in: docTypesRef.current
          .filter((d) => d.doc_type_id)
          .map((d) => d.doc_type_id),
      };
    }

    const appendDateFilter = (startIso, endIso, key) => {
      if (!startIso && !endIso) return;
      const dateFilter = {};
      const gteValue = startIso ? buildLondonMidnightIso(startIso) : '';
      const ltValue = endIso ? buildLondonMidnightIso(endIso, 1) : '';
      if (gteValue) dateFilter.$gte = gteValue;
      if (ltValue) dateFilter.$lt = ltValue;
      if (Object.keys(dateFilter).length > 0) {
        filter[key] = dateFilter;
      }
    };

    appendDateFilter(invoiceStartDate, invoiceEndDate, 'invoice_date');
    appendDateFilter(uploadedStartDate, uploadedEndDate, 'uploaded_on');
    appendDateFilter(createdStartDate, createdEndDate, 'created_on');

    if (discountValue !== '' && discountValue !== null && discountValue !== undefined) {
      const parsedDiscount = Number(discountValue);
      if (Number.isFinite(parsedDiscount)) {
        const operatorMap = {
          '<': '$lt',
          '<=': '$lte',
          '>': '$gt',
          '>=': '$gte',
        };
        const operatorKey = operatorMap[discountOperator] || '$lt';
        filter.discount = { [operatorKey]: parsedDiscount };
      }
    }

    if (includeInNetTotalsFilter !== 'any') {
      filter.include_in_net_totals = includeInNetTotalsFilter === 'true';
    }

    if (creditRequestedFilter !== 'any') {
      filter.credit_requested = creditRequestedFilter === 'true';
    }

    return Object.keys(filter).length > 0 ? filter : null;
  };

  const fetchData = useCallback(async (page = 1, { showPageSpinner = false } = {}) => {
    const startLoading = showPageSpinner ? setPageLoading : setDocLoading;
    startLoading(true);
    isFetchingPageRef.current = true;
    try {
      const queryObject = { page, limit: pageLimit, projection: ['id', 'invoice_date', 'invoice_number', 'job_number', 'location_id', 'repair_location_id', 'supplier_id', 'nominal_code_id', 'doc_status_id', 'doc_reject_reason_id', 'total', 'doc_type_id', 'images', 'created_on', 'uploaded_on', 'discount', 'include_in_net_totals', 'credit_requested'] };
      if (sortCriteria) queryObject.sort = { [sortCriteria]: sortOrder };

      const serverFilter = buildServerFilter();
      if (serverFilter) queryObject.filter = serverFilter;

      const queryBase64 = await fetchBase64(queryObject);

      const [
        uploadsResponse,
        locationsResponse,
        suppliersResponse,
        nominalCodesResponse,
        taxCodesResponse,
        docStatusResponse,
        docTypeResponse,
        docRejectResponse,
      ] = await Promise.all([
        axiosInstance.get(`/api/v1/doc?query=${queryBase64}`),
        axiosInstance.get(`/api/v1/location`),
        axiosInstance.get(`/api/v1/supplier`),
        axiosInstance.get(`/api/v1/nominal-code`),
        axiosInstance.get(`/api/v1/tax-code`),
        axiosInstance.get(`/api/v1/doc/status`),
        axiosInstance.get(`/api/v1/doc/type`),
        axiosInstance.get(`/api/v1/doc/reject`),
      ]);

      const formattedDocuments = uploadsResponse.data.data.documents.map((doc) => ({
        ...doc,
        invoice_date: formatDate(doc.invoice_date),
        posting_date: formatDate(doc.posting_date),
        created_on: formatDate(doc.created_on),
        uploaded_on: formatDate(doc.uploaded_on),
      }));

      const totalDocs = uploadsResponse.data.data.total_documents || 0;
      const netTotal = uploadsResponse.data.data.net_total ?? 0;
      const totalPages = Math.ceil(totalDocs / pageLimit);

      setDataList(formattedDocuments);
      setFilteredDataList(formattedDocuments);
      filterData({
        dataset: formattedDocuments,
        supplier: selectedSupplier,
        supplierRangeStart: supplierStartChar,
        supplierRangeEnd: supplierEndChar,
        supplierList: suppliersResponse.data.data || [],
        locationId: selectedLocation,
        repairLocationId: selectedRepairLocation,
        statuses: selectedDocStatuses,
        types: selectedDocTypes,
        invoiceStart: invoiceStartDate,
        invoiceEnd: invoiceEndDate,
        uploadedStart: uploadedStartDate,
        uploadedEnd: uploadedEndDate,
        createdStart: createdStartDate,
        createdEnd: createdEndDate,
        jobNumber: jobNumberFilter,
        nominalCodeId: selectedNominalCode,
        discountOp: discountOperator,
        discountVal: discountValue,
        includeInNetTotals: includeInNetTotalsFilter,
        creditRequested: creditRequestedFilter,
        resetPage: false,
        syncDocumentView: false,
      });
      setLocations(locationsResponse.data.data || []);
      setSuppliers(suppliersResponse.data.data || []);
      setNominalCodes(nominalCodesResponse.data.data || []);
      setTaxCodes(taxCodesResponse.data.data || []);

      const filteredStatuses = (docStatusResponse.data.data.filter((s) => allowedDocStatuses.includes(s.doc_status_id)) || [])
        .slice()
        .sort((a, b) => a.doc_status_id.localeCompare(b.doc_status_id));
      const filteredTypes = (docTypeResponse.data.data.filter((t) => t.doc_type_id) || [])
        .slice()
        .sort((a, b) => a.doc_type_id.localeCompare(b.doc_type_id));

      const statusChanged =
        filteredStatuses.length !== docStatusesRef.current.length ||
        filteredStatuses.some((status, index) => status.doc_status_id !== docStatusesRef.current[index]?.doc_status_id);
      const typeChanged =
        filteredTypes.length !== docTypesRef.current.length ||
        filteredTypes.some((type, index) => type.doc_type_id !== docTypesRef.current[index]?.doc_type_id);

      if (statusChanged) setDocStatuses(filteredStatuses);
      if (typeChanged) setDocTypes(filteredTypes);
      setDocRejectReasons(docRejectResponse.data.data || []);
      setTotalDocuments(totalDocs);
      setTotalNetValue(netTotal);
      setCurrentDataOffset((page - 1) * pageLimit);
      setTotalPages(totalPages);
      startLoading(false);
      isFetchingPageRef.current = false;
      return formattedDocuments;
    } catch (error) {
      startLoading(false);
      isFetchingPageRef.current = false;
      console.error('Error fetching data:', error);
    }
  }, [
    sortCriteria,
    sortOrder,
    selectedSupplier,
    selectedLocation,
    selectedRepairLocation,
    selectedDocStatuses,
    selectedDocTypes,
    allowedDocStatuses,
    docSearchQuery,
    jobNumberFilter,
    selectedNominalCode,
    invoiceStartDate,
    invoiceEndDate,
    uploadedStartDate,
    uploadedEndDate,
    createdStartDate,
    createdEndDate,
    pageLimit,
    supplierStartChar,
    supplierEndChar,
    discountOperator,
    discountValue,
    includeInNetTotalsFilter,
    creditRequestedFilter,
  ]);

  const fetchBase64 = async (queryObject) => {
    try {
      const response = await axiosInstance.post(`/api/v1/text/base64`, queryObject);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching base64:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }

    const usePageSpinner = initialPageLoadRef.current;
    if (initialPageLoadRef.current) {
      initialPageLoadRef.current = false;
    }

    fetchData(currentPage, { showPageSpinner: usePageSpinner });
  }, [currentPage, fetchData]);

  const handleCardClick = async (id, meta = {}) => {
    const { suppressAutoEffect = false, globalIndex } = meta;
    if (suppressAutoEffect) {
      suppressAutoNavigationRef.current = true;
    }
    activeDocumentIdRef.current = id;
    const requestId = latestDocumentRequestRef.current + 1;
    latestDocumentRequestRef.current = requestId;

    if (typeof globalIndex === 'number') {
      setCurrentDocumentGlobalIndex(globalIndex);
    } else {
      const targetDoc = sortedFilteredDataList.find((doc) => doc.id === id);
      if (targetDoc && typeof targetDoc.__sequenceIndex === 'number') {
        setCurrentDocumentGlobalIndex((prev) => (prev !== targetDoc.__sequenceIndex ? targetDoc.__sequenceIndex : prev));
      }
    }

    setDocLoading(true);
    try {
      const response = await axiosInstance.get(`/api/v1/doc/${id}`);
      if (requestId !== latestDocumentRequestRef.current || activeDocumentIdRef.current !== id) {
        return;
      }
      const data = response.data.data;

      const formattedData = {
        ...data,
        invoice_date: formatDate(data.invoice_date),
        uploaded_on: formatDate(data.uploaded_on),
        net: parseFloat(data.net).toFixed(2),
        vat: parseFloat(data.vat).toFixed(2),
        total: parseFloat(data.total).toFixed(2),
      };

      setDetailedData(formattedData);
      setEditableData(formattedData);
      setTempEditableData(formattedData);
      setCurrentImageIndex(0);
      zoomOut();
      setDocLoading(false);
      suppressAutoNavigationRef.current = false;
    } catch (error) {
      if (requestId !== latestDocumentRequestRef.current || activeDocumentIdRef.current !== id) {
        return;
      }
      setDocLoading(false);
      suppressAutoNavigationRef.current = false;
      console.error('Error fetching detailed data:', error);
    }
  };

  useEffect(() => {
    if (!detailedData) return;
    const targetDoc = sortedFilteredDataList.find((doc) => doc.id === detailedData.id);
    if (targetDoc && typeof targetDoc.__sequenceIndex === 'number') {
      setCurrentDocumentGlobalIndex((prev) => {
        if (prev >= 0 && prev !== targetDoc.__sequenceIndex) {
          return prev;
        }
        return prev !== targetDoc.__sequenceIndex ? targetDoc.__sequenceIndex : prev;
      });
    }
  }, [sortedFilteredDataList, detailedData]);

  const getImageName = () => {
    const matchingDoc = sortedFilteredDataList.find((doc) => doc.id === detailedData?.id);
    const fullImageName = matchingDoc?.images?.[0] || 'No Image';
    const imageNameParts = fullImageName.split('_');
    return imageNameParts.length > 1 ? imageNameParts[1] : fullImageName;
    };

  const normaliseDocumentForLists = useCallback((doc) => {
    if (!doc || !doc.id) return null;

    const normalisedTotal = (() => {
      if (doc.total === null || doc.total === undefined || doc.total === '') return '';
      const numericValue = Number(doc.total);
      if (Number.isFinite(numericValue)) {
        return numericValue.toFixed(2);
      }
      return doc.total;
    })();

    return {
      ...doc,
      total: normalisedTotal,
      images: Array.isArray(doc.images) ? doc.images : [],
    };
  }, []);

  const documentMatchesActiveFilters = useCallback((doc) => {
    if (!doc || !doc.id) return false;

    const invoiceNumber = (doc.invoice_number ?? '').toString().toLowerCase();
    const jobNumber = (doc.job_number ?? '').toString().toLowerCase();

    if (docSearchQuery) {
      const query = docSearchQuery.toLowerCase();
      if (!invoiceNumber.includes(query)) return false;
    }

    const trimmedJobFilter = jobNumberFilter ? jobNumberFilter.trim().toLowerCase() : '';
    if (trimmedJobFilter && !jobNumber.includes(trimmedJobFilter)) return false;

    if (selectedSupplier && doc.supplier_id !== selectedSupplier) return false;
    if (selectedLocation && doc.location_id !== selectedLocation) return false;
    if (selectedRepairLocation && doc.repair_location_id !== selectedRepairLocation) return false;
    if (selectedNominalCode && doc.nominal_code_id !== selectedNominalCode) return false;

    let statusesToUse = Array.isArray(selectedDocStatuses) ? selectedDocStatuses : [];
    if (statusesToUse.length === 0 && Array.isArray(docStatuses) && docStatuses.length > 0) {
      statusesToUse = docStatuses
        .filter((status) => allowedDocStatuses.includes(status.doc_status_id))
        .map((status) => status.doc_status_id);
    }
    if (statusesToUse.length > 0 && (!doc.doc_status_id || !statusesToUse.includes(doc.doc_status_id))) {
      return false;
    }

    let typesToUse = Array.isArray(selectedDocTypes) ? selectedDocTypes : [];
    if (typesToUse.length === 0 && Array.isArray(docTypes) && docTypes.length > 0) {
      typesToUse = docTypes
        .filter((type) => type.doc_type_id)
        .map((type) => type.doc_type_id);
    }
    if (typesToUse.length > 0 && (!doc.doc_type_id || !typesToUse.includes(doc.doc_type_id))) {
      return false;
    }

    const withinDateRange = (value, startIso, endIso) => {
      if (!startIso && !endIso) return true;
      const docDate = parseDisplayDate(value);
      if (!docDate) return true;

      if (startIso) {
        const startDate = parseIsoDate(startIso);
        if (startDate && docDate < startDate) return false;
      }

      if (endIso) {
        const endDate = parseIsoDate(endIso);
        if (endDate) {
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (docDate > endOfDay) return false;
        }
      }

      return true;
    };

    if (!withinDateRange(doc.invoice_date, invoiceStartDate, invoiceEndDate)) return false;
    if (!withinDateRange(doc.uploaded_on, uploadedStartDate, uploadedEndDate)) return false;
    if (!withinDateRange(doc.created_on, createdStartDate, createdEndDate)) return false;

    if (discountValue !== '' && discountValue !== null && discountValue !== undefined) {
      const parsedDiscount = Number(discountValue);
      if (Number.isFinite(parsedDiscount)) {
        const docDiscount = Number(doc.discount);
        if (!Number.isFinite(docDiscount)) return false;
        switch (discountOperator) {
          case '<':
            if (!(docDiscount < parsedDiscount)) return false;
            break;
          case '>':
            if (!(docDiscount > parsedDiscount)) return false;
            break;
          case '>=':
            if (!(docDiscount >= parsedDiscount)) return false;
            break;
          case '<=':
          default:
            if (!(docDiscount <= parsedDiscount)) return false;
        }
      }
    }

    return true;
  }, [
    allowedDocStatuses,
    createdEndDate,
    createdStartDate,
    discountOperator,
    discountValue,
    docSearchQuery,
    docStatuses,
    docTypes,
    invoiceEndDate,
    invoiceStartDate,
    jobNumberFilter,
    selectedDocStatuses,
    selectedDocTypes,
    selectedLocation,
    selectedRepairLocation,
    selectedNominalCode,
    selectedSupplier,
    uploadedEndDate,
    uploadedStartDate,
  ]);

  const handleFieldChange = (field, value) => {
    setTempEditableData((prev) => {
      if (!prev) return prev;

      const next = { ...prev, [field]: value };

      if (field === 'net' || field === 'vat') {
        const net = parseFloat(field === 'net' ? value : next.net) || 0;
        const vat = parseFloat(field === 'vat' ? value : next.vat) || 0;
        next.total = (net + vat).toFixed(2);
      }

      return next;
    });

    setIsEdited(true);
  };

  const handleFieldBlur = (field, value) => {
    const formattedValue = parseFloat(value).toFixed(2);
    setTempEditableData((prev) => ({ ...prev, [field]: formattedValue }));
  };

  const closeDocumentView = useCallback(({ refetch = true } = {}) => {
    activeDocumentIdRef.current = null;
    latestDocumentRequestRef.current += 1;
    setDetailedData(null);
    setEditableData(null);
    setTempEditableData(null);
    setIsEdited(false);
    setCurrentDocumentGlobalIndex(-1);
    if (refetch) {
      fetchData(currentPage, { showPageSpinner: true });
    }
  }, [currentPage, fetchData]);

  const handleBackClick = () => {
    closeDocumentView();
  };

  const handleSupplierChange = (event) => {
    const val = event.target.value;
    setSelectedSupplier(val);
    setSupplierStartChar('');
    setSupplierEndChar('');
    filterData({ supplier: val, supplierRangeStart: '', supplierRangeEnd: '' });
  };

  const handleLocationChange = (event) => {
    const val = event.target.value;
    setSelectedLocation(val);
    filterData({ locationId: val });
  };

  const handleRepairLocationChange = (event) => {
    const val = event.target.value;
    setSelectedRepairLocation(val);
    filterData({ repairLocationId: val });
  };

  const handleSupplierStartCharChange = (value) => {
    const normalisedValue = normaliseLetter(value);
    let nextEndChar = supplierEndChar;

    if (!normalisedValue) {
      nextEndChar = '';
    } else if (nextEndChar && nextEndChar < normalisedValue) {
      nextEndChar = normalisedValue;
    }

    setSupplierStartChar(normalisedValue);
    setSupplierEndChar(nextEndChar);
    setSelectedSupplier('');
    filterData({ supplier: '', supplierRangeStart: normalisedValue, supplierRangeEnd: nextEndChar });
  };

  const handleSupplierEndCharChange = (value) => {
    const normalisedValue = normaliseLetter(value);

    if (!normalisedValue) {
      setSupplierEndChar('');
      setSelectedSupplier('');
      filterData({ supplier: '', supplierRangeStart: supplierStartChar, supplierRangeEnd: '' });
      return;
    }

    const effectiveStart = normaliseLetter(supplierStartChar) || 'A';
    const finalValue = normalisedValue < effectiveStart ? effectiveStart : normalisedValue;
    setSupplierEndChar(finalValue);
    setSelectedSupplier('');
    filterData({ supplier: '', supplierRangeStart: supplierStartChar, supplierRangeEnd: finalValue });
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleDocStatusChange = (selectedStatuses) => {
    setSelectedDocStatuses(selectedStatuses);
    filterData({ statuses: selectedStatuses });
  };

  const handleDocTypeChange = (selectedTypes) => {
    setSelectedDocTypes(selectedTypes);
    filterData({ types: selectedTypes });
  };

  const handleNominalCodeChange = (value) => {
    const nextValue = value || '';
    setSelectedNominalCode(nextValue);
    filterData({ nominalCodeId: nextValue });
  };

  const handleIncludeInNetTotalsFilterChange = (value) => {
    const nextValue = value || 'any';
    setIncludeInNetTotalsFilter(nextValue);
    filterData({ includeInNetTotals: nextValue });
  };

  const handleCreditRequestedFilterChange = (value) => {
    const nextValue = value || 'any';
    setCreditRequestedFilter(nextValue);
    filterData({ creditRequested: nextValue });
  };

  const handleClearFilters = () => {
    setSelectedSupplier('');
    setSelectedLocation('');
    setSelectedRepairLocation('');
    setSupplierStartChar('');
    setSupplierEndChar('');
    setSelectedDocStatuses(defaultSelectedDocStatuses);
    setSelectedDocTypes(defaultSelectedDocTypes);
    setSortCriteria('');
    setSortOrder('asc');
    setFilteredDataList(dataList);
    setDocSearchQuery('');
    setInvoiceStartDate('');
    setInvoiceEndDate('');
    setUploadedStartDate('');
    setUploadedEndDate('');
    setCreatedStartDate('');
    setCreatedEndDate('');
    setJobNumberFilter('');
    setSelectedNominalCode('');
    setDiscountOperator('<');
    setDiscountValue('');
    setIncludeInNetTotalsFilter('any');
    setCreditRequestedFilter('any');
  };

  // 🔎 NEW: search callback (called from GridView)
  const handleSearchDocument = (docNumber) => {
    const query = (docNumber || '').toString().trim();

    if (query) {
      setSelectedSupplier('');
      setSelectedLocation('');
      setSelectedRepairLocation('');
      setSupplierStartChar('');
      setSupplierEndChar('');
      setSelectedNominalCode('');
      setSelectedDocStatuses(
        docStatusesRef.current.length
          ? docStatusesRef.current.map((status) => status.doc_status_id)
          : defaultSelectedDocStatuses
      );
      setSelectedDocTypes(
        docTypesRef.current.length
          ? docTypesRef.current.map((type) => type.doc_type_id)
          : defaultSelectedDocTypes
      );
      setSortCriteria('');
      setSortOrder('asc');
      setInvoiceStartDate('');
      setInvoiceEndDate('');
      setUploadedStartDate('');
      setUploadedEndDate('');
      setCreatedStartDate('');
      setCreatedEndDate('');
      setJobNumberFilter('');
      setIncludeInNetTotalsFilter('any');
      setCreditRequestedFilter('any');
    }

    setDocSearchQuery(query);
    setCurrentPage(1);
  };

  const handleSearchJobNumber = (jobNumber) => {
    const query = (jobNumber || '').toString().trim();

    if (query) {
      setSelectedSupplier('');
      setSelectedLocation('');
      setSelectedRepairLocation('');
      setSupplierStartChar('');
      setSupplierEndChar('');
      setSelectedNominalCode('');
      setSelectedDocStatuses(
        docStatusesRef.current.length
          ? docStatusesRef.current.map((status) => status.doc_status_id)
          : defaultSelectedDocStatuses
      );
      setSelectedDocTypes(
        docTypesRef.current.length
          ? docTypesRef.current.map((type) => type.doc_type_id)
          : defaultSelectedDocTypes
      );
      setSortCriteria('');
      setSortOrder('asc');
      setInvoiceStartDate('');
      setInvoiceEndDate('');
      setUploadedStartDate('');
      setUploadedEndDate('');
      setCreatedStartDate('');
      setCreatedEndDate('');
      setDocSearchQuery('');
      setDiscountOperator('<');
      setDiscountValue('');
      setIncludeInNetTotalsFilter('any');
      setCreditRequestedFilter('any');
    }

    setJobNumberFilter(query);
    setCurrentPage(1);
  };

  const filterData = ({
    supplier = selectedSupplier,
    supplierRangeStart = supplierStartChar,
    supplierRangeEnd = supplierEndChar,
    locationId = selectedLocation,
    repairLocationId = selectedRepairLocation,
    statuses = selectedDocStatuses,
    types = selectedDocTypes,
    supplierList = suppliers,
    invoiceStart = invoiceStartDate,
    invoiceEnd = invoiceEndDate,
    uploadedStart = uploadedStartDate,
    uploadedEnd = uploadedEndDate,
    createdStart = createdStartDate,
    createdEnd = createdEndDate,
    jobNumber = jobNumberFilter,
    nominalCodeId = selectedNominalCode,
    discountOp = discountOperator,
    discountVal = discountValue,
    includeInNetTotals = includeInNetTotalsFilter,
    creditRequested = creditRequestedFilter,
    dataset = dataList,
    resetPage = true,
    syncDocumentView = true,
  } = {}) => {
    let filtered = dataset;

    if (docSearchQuery) {
      const q = docSearchQuery.toLowerCase();
      filtered = filtered.filter((d) =>
        (d.invoice_number || '').toString().toLowerCase().includes(q)
      );
    }
    const trimmedJobNumber = jobNumber ? jobNumber.trim() : '';
    if (trimmedJobNumber) {
      const q = trimmedJobNumber.toLowerCase();
      filtered = filtered.filter((d) => (d.job_number || '').toString().toLowerCase().includes(q));
    }
    if (supplier) filtered = filtered.filter((d) => d.supplier_id === supplier);
    if (locationId) filtered = filtered.filter((d) => d.location_id === locationId);
    if (repairLocationId) filtered = filtered.filter((d) => d.repair_location_id === repairLocationId);
    if (nominalCodeId) filtered = filtered.filter((d) => d.nominal_code_id === nominalCodeId);
    if (includeInNetTotals !== 'any') {
      const expected = includeInNetTotals === 'true';
      filtered = filtered.filter((d) => Boolean(d.include_in_net_totals) === expected);
    }
    if (creditRequested !== 'any') {
      const expected = creditRequested === 'true';
      filtered = filtered.filter((d) => Boolean(d.credit_requested) === expected);
    }

    if (discountVal !== '' && discountVal !== null && discountVal !== undefined) {
      const parsedDiscount = Number(discountVal);
      if (Number.isFinite(parsedDiscount)) {
        filtered = filtered.filter((d) => {
          const docDiscount = Number(d.discount);
          if (!Number.isFinite(docDiscount)) return false;
          switch (discountOp) {
            case '<':
              return docDiscount < parsedDiscount;
            case '>':
              return docDiscount > parsedDiscount;
            case '>=':
              return docDiscount >= parsedDiscount;
            case '<=':
            default:
              return docDiscount <= parsedDiscount;
          }
        });
      }
    }

    const rawStartLetter = normaliseLetter(supplierRangeStart);
    const rawEndLetter = normaliseLetter(supplierRangeEnd);
    const hasSupplierRangeFilter = Boolean(rawStartLetter) || Boolean(rawEndLetter);
    const startLetter = rawStartLetter || 'A';
    const endLetter = rawEndLetter || 'Z';
    if (hasSupplierRangeFilter) {
      const suppliersInRange = getSupplierIdsInLetterRange(startLetter, endLetter, supplierList);
      if (suppliersInRange.length === 0) {
        filtered = [];
      } else {
        const supplierSet = new Set(suppliersInRange);
        filtered = filtered.filter((d) => supplierSet.has(d.supplier_id));
      }
    }

    if (statuses) {
      let statusesToUse = statuses;
      if (statusesToUse.length === 0) statusesToUse = docStatuses.map((d) => d.doc_status_id);
      filtered = filtered.filter((d) => statusesToUse.includes(d.doc_status_id));
    }

    if (types) {
      let typesToUse = types;
      if (typesToUse.length === 0) typesToUse = docTypes.map((d) => d.doc_type_id);
      filtered = filtered.filter((d) => typesToUse.includes(d.doc_type_id));
    }

    const applyDateFilter = (records, startIso, endIso, valueAccessor) => {
      const startDate = parseIsoDate(startIso);
      const endDate = parseIsoDate(endIso);
      if (!startDate && !endDate) return records;
      const endOfDay = endDate ? new Date(endDate.getTime()) : null;
      if (endOfDay) {
        endOfDay.setHours(23, 59, 59, 999);
      }
      return records.filter((record) => {
        const value = valueAccessor(record);
        const docDate = parseDisplayDate(value);
        if (!docDate) return true;
        if (startDate && docDate < startDate) return false;
        if (endOfDay && docDate > endOfDay) return false;
        return true;
      });
    };

    filtered = applyDateFilter(filtered, invoiceStart, invoiceEnd, (record) => record.invoice_date);
    filtered = applyDateFilter(filtered, uploadedStart, uploadedEnd, (record) => record.uploaded_on);
    filtered = applyDateFilter(filtered, createdStart, createdEnd, (record) => record.created_on);

    setFilteredDataList(filtered);
    if (syncDocumentView && detailedData) {
      pendingFilterSyncRef.current = true;
    }
    if (resetPage) {
      setCurrentPage(1);
    }
  };

  const handleInvoiceStartDateChange = (value) => {
    if (value && !isValidIsoDate(value)) {
      return;
    }
    nextInvoiceStartDateRef.current = value;
    setInvoiceStartDate(value);
    filterData({ invoiceStart: value });
  };

  const handleDiscountOperatorChange = (value) => {
    setDiscountOperator(value);
    filterData({ discountOp: value });
  };

  const handleDiscountValueChange = (value) => {
    setDiscountValue(value);
    filterData({ discountVal: value });
  };

  const handleInvoiceEndDateChange = (value) => {
    if (value && !isValidIsoDate(value)) {
      return;
    }
    setInvoiceEndDate(value);
    const startForFilter = nextInvoiceStartDateRef.current ?? invoiceStartDate;
    filterData({ invoiceStart: startForFilter, invoiceEnd: value });
  };

  const handleUploadedStartDateChange = (value) => {
    if (value && !isValidIsoDate(value)) {
      return;
    }
    nextUploadedStartDateRef.current = value;
    setUploadedStartDate(value);
    filterData({ uploadedStart: value });
  };

  const handleUploadedEndDateChange = (value) => {
    if (value && !isValidIsoDate(value)) {
      return;
    }
    setUploadedEndDate(value);
    const startForFilter = nextUploadedStartDateRef.current ?? uploadedStartDate;
    filterData({ uploadedStart: startForFilter, uploadedEnd: value });
  };

  const handleCreatedStartDateChange = (value) => {
    if (value && !isValidIsoDate(value)) {
      return;
    }
    nextCreatedStartDateRef.current = value;
    setCreatedStartDate(value);
    filterData({ createdStart: value });
  };

  const handleCreatedEndDateChange = (value) => {
    if (value && !isValidIsoDate(value)) {
      return;
    }
    setCreatedEndDate(value);
    const startForFilter = nextCreatedStartDateRef.current ?? createdStartDate;
    filterData({ createdStart: startForFilter, createdEnd: value });
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    try {
      setExporting(true);
      const exportLimit = totalDocuments > 0 ? totalDocuments : pageLimit;
      const queryObject = {
        page: 1,
        limit: Math.max(exportLimit, pageLimit),
        projection: [
          'repair_location_id',
          'supplier_id',
          'nominal_code_id',
          'doc_type_id',
          'invoice_date',
          'uploaded_on',
          'job_number',
          'invoice_number',
          'order_reference',
          'net',
          'vat',
          'total',
          'discount',
          'credit_amount_requested',
          'credit_amount_received',
        ],
      };

      if (sortCriteria) {
        queryObject.sort = { [sortCriteria]: sortOrder };
      }

      const serverFilter = buildServerFilter();
      if (serverFilter) {
        queryObject.filter = serverFilter;
      }

      const queryBase64 = await fetchBase64(queryObject);
      const exportResponse = await axiosInstance.get(`/api/v1/doc?query=${queryBase64}`);
      const documents = exportResponse?.data?.data?.documents || [];

      if (!documents.length) {
        setNotification({ message: 'No records to export for the current filter.', type: 'info', open: true });
        return;
      }

      const locationMap = locations.reduce((acc, loc) => {
        acc[loc.location_id] = loc.name;
        return acc;
      }, {});

      const supplierMap = suppliers.reduce((acc, sup) => {
        acc[sup.supplier_id] = sup.name;
        return acc;
      }, {});

      const docTypeMap = docTypes.reduce((acc, type) => {
        acc[type.doc_type_id] = type.name;
        return acc;
      }, {});

      const headers = [
        'Job Number',
        'Repair Location',
        'Supplier',
        'Nominal Code',
        'Document Type',
        'Document Date',
        'Upload Date',
        'Days Difference',
        'Document Number',
        'Order Number',
        'Net',
        'VAT',
        'Total',
        'Discount',
        'Credit Requested',
        'Credit Received',
      ];

      const escapeCsv = (value) => {
        const stringValue = value === null || value === undefined ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const formatAmount = (value, makeNegative = false) => {
        if (value === null || value === undefined || value === '') return '';
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) return String(value);
        const adjusted = makeNegative ? -Math.abs(numericValue) : numericValue;
        return adjusted.toFixed(2);
      };

      const formatPercent = (value) => {
        if (value === null || value === undefined || value === '') return '';
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) return String(value);
        return numericValue.toFixed(2);
      };

      const rows = documents.map((doc) => {
        const jobNumber = doc.job_number || '';
        const locationName = locationMap[doc.repair_location_id] || doc.repair_location_id || '';
        const supplierName = supplierMap[doc.supplier_id] || doc.supplier_id || '';
        const nominalName = nominalCodes.find((code) => code.nominal_code_id === doc.nominal_code_id)?.name
          || doc.nominal_code_id
          || '';
        const documentTypeName = docTypeMap[doc.doc_type_id] || doc.doc_type_id || '';
        const documentDate = doc.invoice_date ? formatDate(doc.invoice_date) : '';
        const uploadDate = doc.uploaded_on ? formatDate(doc.uploaded_on) : '';
        const daysDifference = (() => {
          if (!doc.invoice_date || !doc.uploaded_on) return '';
          const docDate = new Date(doc.invoice_date);
          const upload = new Date(doc.uploaded_on);
          if (Number.isNaN(docDate.getTime()) || Number.isNaN(upload.getTime())) return '';
          const diffMs = upload.getTime() - docDate.getTime();
          return Math.round(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
        })();
        const documentNumber = doc.invoice_number || '';
        const orderNumber = doc.order_reference || '';
        const isCreditNote = (doc.doc_type_id || '').toLowerCase() === 'credit_note';
        const netValue = formatAmount(doc.net, isCreditNote);
        const vatValue = formatAmount(doc.vat, isCreditNote);
        const totalValue = formatAmount(doc.total, isCreditNote);
        const discountValue = formatPercent(doc.discount);
        const creditRequestedValue = formatAmount(doc.credit_amount_requested, isCreditNote);
        const creditReceivedValue = formatAmount(doc.credit_amount_received, isCreditNote);

        return [
          jobNumber,
          locationName,
          supplierName,
          nominalName,
          documentTypeName,
          documentDate,
          uploadDate,
          daysDifference,
          documentNumber,
          orderNumber,
          netValue,
          vatValue,
          totalValue,
          discountValue,
          creditRequestedValue,
          creditReceivedValue,
        ].map(escapeCsv).join(',');
      });

      const csvContent = [headers.map(escapeCsv).join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `documents-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({ message: 'Export complete. CSV downloaded.', type: 'success', open: true });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setNotification({ message: 'Failed to export CSV. Please try again.', type: 'error', open: true });
    } finally {
      setExporting(false);
    }
  };

  const updateInvoice = async (updatedData) => {
    const formattedData = {
      location_id: updatedData.location_id,
      supplier_id: updatedData.supplier_id,
      nominal_code_id: updatedData.nominal_code_id || null,
      tax_code_id: updatedData.tax_code_id || null,
      doc_status_id: updatedData.doc_status_id,
      doc_type_id: updatedData.doc_type_id,
      invoice_date: revertDateFormat(updatedData.invoice_date) || null,
      posting_date: revertDateFormat(updatedData.posting_date) || null,
      invoice_number: updatedData.invoice_number,
      order_number: updatedData.order_number,
      net: parseFloat(updatedData.net),
      vat: parseFloat(updatedData.vat),
      total: parseFloat(updatedData.total),
      notes: updatedData.notes,
      include_in_net_totals: Boolean(updatedData.include_in_net_totals),
      credit_requested: Boolean(updatedData.credit_requested),
      credit_amount_requested: Number(updatedData.credit_amount_requested || 0),
      credit_amount_received: Number(updatedData.credit_amount_received || 0),
      credit_note: updatedData.credit_note,
      doc_reject_reason_id: updatedData.doc_reject_reason_id || null,
      id: updatedData.id,
      email_uploader: Boolean(updatedData.email_uploader),
    };

    try {
      const response = await axiosInstance.put(`/api/v1/doc`, formattedData, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  };

  const advanceToNextDocument = async ({ docStillVisible = true } = {}) => {
    try {
      let targetGlobalIndex = currentDocumentGlobalIndex < 0 ? 0 : currentDocumentGlobalIndex;
      if (docStillVisible) {
        targetGlobalIndex += 1;
      }

      let targetPage = Math.max(1, Math.floor(targetGlobalIndex / pageLimit) + 1);
      let pageDocuments = await fetchData(targetPage, { showPageSpinner: false });
      if (!Array.isArray(pageDocuments)) {
        pageDocuments = [];
      }

      while (pageDocuments.length === 0 && targetPage > 1) {
        targetPage -= 1;
        pageDocuments = await fetchData(targetPage, { showPageSpinner: false });
        if (!Array.isArray(pageDocuments)) {
          pageDocuments = [];
        }
      }

      if (pageDocuments.length === 0) {
        handleBackClick();
        return;
      }

      const pageOffset = (targetPage - 1) * pageLimit;
      const maxGlobalIndexOnPage = pageOffset + pageDocuments.length - 1;

      if (targetGlobalIndex < pageOffset) {
        targetGlobalIndex = pageOffset;
      }
      if (targetGlobalIndex > maxGlobalIndexOnPage) {
        targetGlobalIndex = maxGlobalIndexOnPage;
      }

      let localIndex = Math.max(0, targetGlobalIndex - pageOffset);
      if (localIndex >= pageDocuments.length) {
        localIndex = pageDocuments.length - 1;
      }

      let targetDoc = pageDocuments[localIndex];

      if (targetDoc?.id === tempEditableData?.id) {
        if (localIndex + 1 < pageDocuments.length) {
          localIndex += 1;
          targetGlobalIndex = pageOffset + localIndex;
          targetDoc = pageDocuments[localIndex];
        } else if (localIndex > 0) {
          localIndex -= 1;
          targetGlobalIndex = pageOffset + localIndex;
          targetDoc = pageDocuments[localIndex];
        } else {
          handleBackClick();
          return;
        }
      }

      if (!targetDoc?.id) {
        handleBackClick();
        return;
      }

      if (targetPage !== currentPage) {
        suppressFetchRef.current = true;
        setCurrentPage(targetPage);
      }

      await handleCardClick(targetDoc.id, {
        globalIndex: pageOffset + localIndex,
        suppressAutoEffect: true,
      });
    } catch (error) {
      console.error('Error advancing to the next document:', error);
    }
  };

  const handleSaveChanges = async () => {
    const formattedData = {
      ...tempEditableData,
      net: parseFloat(tempEditableData.net).toFixed(2),
      vat: parseFloat(tempEditableData.vat).toFixed(2),
      total: parseFloat(tempEditableData.total).toFixed(2),
    };

    try {
      await updateInvoice(formattedData);
      setNotification({ message: 'Changes saved successfully', type: 'success', open: true });
      const stillMatchesFilters = documentMatchesActiveFilters(formattedData);

      setEditableData(formattedData);
      setTempEditableData(formattedData);
      setDetailedData(formattedData);
      setIsEdited(false);

      let pageToUse = currentPage;
      let pageDocuments = await fetchData(pageToUse, { showPageSpinner: false });

      if (!Array.isArray(pageDocuments)) {
        pageDocuments = [];
      }

      if (!stillMatchesFilters) {
        if (pageDocuments.length === 0 && pageToUse > 1) {
          pageToUse = pageToUse - 1;
          pageDocuments = await fetchData(pageToUse, { showPageSpinner: false });
          if (!Array.isArray(pageDocuments)) {
            pageDocuments = [];
          }
          setCurrentPage(pageToUse);
        }

        if (Array.isArray(pageDocuments) && pageDocuments.length > 0) {
          const pageOffset = (pageToUse - 1) * pageLimit;
          const localIndex = Math.min(
            Math.max(currentDocumentGlobalIndex - pageOffset, 0),
            pageDocuments.length - 1
          );
          const targetDoc = pageDocuments[localIndex];

          if (targetDoc?.id) {
            await handleCardClick(targetDoc.id, {
              globalIndex: pageOffset + localIndex,
              suppressAutoEffect: true,
            });
            return;
          }
        }

        // No documents left to show under current filters
        if (!Array.isArray(pageDocuments) || pageDocuments.length === 0) {
          setDetailedData(null);
          setEditableData(null);
          setTempEditableData(null);
          setIsEdited(false);
          setCurrentDocumentGlobalIndex(-1);
        }
      }
    } catch (errorData) {
      let errorMessage = 'Submission failed. Please try again.';
      if (errorData.errors && Array.isArray(errorData.errors)) errorMessage = errorData.errors.map((e) => e.msg).join(', ');
      else if (errorData.error) errorMessage = errorData.error;
      setNotification({ message: errorMessage, type: 'error', open: true });
    }
  };

  const APPROVABLE_DOC_TYPES = new Set(['invoice', 'estimate', 'credit_note', 'statement']);

  const handleApproveForImport = async () => {
    if (!tempEditableData?.doc_type_id || !APPROVABLE_DOC_TYPES.has(tempEditableData.doc_type_id)) {
      setNotification({
        message: 'Only Invoice, Estimate, Credit Note, or Statement documents can be approved.',
        type: 'warning',
        open: true,
      });
      return;
    }
    const formattedData = {
      ...tempEditableData,
      doc_status_id: 'approved',
      doc_reject_reason_id: null,
    };
    try {
      await updateInvoice(formattedData);
      setNotification({ message: 'Document approved', type: 'success', open: true });
      await advanceToNextDocument({ docStillVisible: documentMatchesActiveFilters(formattedData) });
    } catch (errorData) {
      let errorMessage = 'Submission failed. Please try again.';
      if (errorData.errors && Array.isArray(errorData.errors)) errorMessage = errorData.errors.map((e) => e.msg).join(', ');
      else if (errorData.error) errorMessage = errorData.error;
      setNotification({ message: errorMessage, type: 'error', open: true });
      await handleCardClick(tempEditableData.id);
    }
  };

  const handleRejectDocument = async ({ statusId = 'rejected', reasonId = null, notes, emailUploader = false } = {}) => {
    const formattedData = {
      ...tempEditableData,
      doc_status_id: statusId || 'rejected',
      doc_reject_reason_id: reasonId || null,
      include_in_net_totals: false,
      credit_requested: false,
      email_uploader: typeof emailUploader === 'boolean' ? emailUploader : Boolean(tempEditableData?.email_uploader),
    };
    if (typeof notes === 'string') {
      formattedData.notes = notes;
    }
    try {
      await updateInvoice(formattedData);
      setNotification({ message: 'Document rejected', type: 'success', open: true });
      await advanceToNextDocument({ docStillVisible: documentMatchesActiveFilters(formattedData) });
    } catch (errorData) {
      let errorMessage = 'Submission failed. Please try again.';
      if (errorData.errors && Array.isArray(errorData.errors)) errorMessage = errorData.errors.map((e) => e.msg).join(', ');
      else if (errorData.error) errorMessage = errorData.error;
      setNotification({ message: errorMessage, type: 'error', open: true });
      await handleCardClick(tempEditableData.id);
    }
  };

  const zoomOut = () => {
    if (transformWrapperRef.current) transformWrapperRef.current.resetTransform();
  };

  const getLocationOptionLabel = (option) => (typeof option === 'string' ? option : `${option.name}`);
  const getSupplierOptionLabel = (option) => (typeof option === 'string' ? option : `${option.supplier_id} - ${option.name}`);
  const getNominalCodeOptionLabel = (option) => (!option ? '' : typeof option === 'string' ? option : `${option.nominal_code_id} - ${option.name}`);
  const getDocTypeOptionLabel = (option) => (!option ? '' : typeof option === 'string' ? option : `${option.doc_type_id} - ${option.name}`);
  const getDocStatusOptionLabel = (option) => (!option ? '' : typeof option === 'string' ? option : `${option.doc_status_id} - ${option.name}`);

  const refetchData = useCallback(async () => {
    const activeDocId = detailedData?.id;
    const activeGlobalIndex = currentDocumentGlobalIndex;
    const originalPage = currentPage;

    suppressAutoNavigationRef.current = true;
    setDocLoading(true);

    try {
      let pageToUse = originalPage;
      let pageDocuments = await fetchData(pageToUse, { showPageSpinner: false });

      if (!Array.isArray(pageDocuments)) {
        pageDocuments = [];
      }

      while (pageDocuments.length === 0 && pageToUse > 1) {
        pageToUse -= 1;
        pageDocuments = await fetchData(pageToUse, { showPageSpinner: false });
        if (!Array.isArray(pageDocuments)) {
          pageDocuments = [];
        }
      }

      if (pageToUse !== originalPage) {
        suppressFetchRef.current = true;
        setCurrentPage(pageToUse);
      }

      if (!activeDocId) {
        return;
      }
      if (activeDocumentIdRef.current && activeDocumentIdRef.current !== activeDocId) {
        return;
      }

      const pageOffset = (pageToUse - 1) * pageLimit;
      const currentDocIndex = pageDocuments.findIndex((doc) => doc.id === activeDocId);

      if (currentDocIndex >= 0) {
        const refreshedCurrentDoc = normaliseDocumentForLists(pageDocuments[currentDocIndex]);
        if (documentMatchesActiveFilters(refreshedCurrentDoc)) {
          if (activeDocumentIdRef.current && activeDocumentIdRef.current !== activeDocId) {
            return;
          }
          await handleCardClick(activeDocId, {
            globalIndex: activeGlobalIndex >= 0 ? activeGlobalIndex : pageOffset + currentDocIndex,
            suppressAutoEffect: true,
          });
          return;
        }
      }

      if (pageDocuments.length > 0) {
        const localIndex = Math.min(
          Math.max(activeGlobalIndex - pageOffset, 0),
          pageDocuments.length - 1
        );
        const targetDoc = pageDocuments[localIndex];

        if (targetDoc?.id) {
          if (activeDocumentIdRef.current && activeDocumentIdRef.current !== activeDocId) {
            return;
          }
          await handleCardClick(targetDoc.id, {
            globalIndex: pageOffset + localIndex,
            suppressAutoEffect: true,
          });
          return;
        }
      }

      setDetailedData(null);
      setEditableData(null);
      setTempEditableData(null);
      setIsEdited(false);
      setCurrentDocumentGlobalIndex(-1);
      activeDocumentIdRef.current = null;
      latestDocumentRequestRef.current += 1;
    } finally {
      suppressAutoNavigationRef.current = false;
      setDocLoading(false);
    }
  }, [
    currentDocumentGlobalIndex,
    currentPage,
    detailedData,
    documentMatchesActiveFilters,
    fetchData,
    handleCardClick,
    normaliseDocumentForLists,
    pageLimit,
  ]);

  const documentSequence = sortedFilteredDataList;
  const hasPreviousDocument = currentDocumentGlobalIndex > 0;
  const hasNextDocument =
    currentDocumentGlobalIndex >= 0 && currentDocumentGlobalIndex < totalDocuments - 1;

  const handleNavigateDocument = useCallback(
    async (direction) => {
      if (!detailedData || direction === 0 || totalDocuments <= 0) return;

      let targetGlobalIndex = currentDocumentGlobalIndex + direction;

      if (!documentMatchesActiveFilters(detailedData)) {
        if (direction > 0) {
          const nextDoc = documentSequence.find((doc) => doc.__sequenceIndex > currentDocumentGlobalIndex);
          if (nextDoc) {
            targetGlobalIndex = nextDoc.__sequenceIndex;
          }
        } else if (direction < 0) {
          const previousDoc = [...documentSequence]
            .reverse()
            .find((doc) => doc.__sequenceIndex < currentDocumentGlobalIndex);
          if (previousDoc) {
            targetGlobalIndex = previousDoc.__sequenceIndex;
          }
        }
      }

      if (targetGlobalIndex < 0 || targetGlobalIndex >= totalDocuments) return;

      const targetPage = Math.floor(targetGlobalIndex / pageLimit) + 1;
      const targetIndexWithinPage = targetGlobalIndex % pageLimit;

      if (targetPage === currentPage && documentSequence[targetIndexWithinPage]) {
        const targetDoc = documentSequence[targetIndexWithinPage];
        if (targetDoc?.id) {
          await handleCardClick(targetDoc.id, {
            globalIndex: typeof targetDoc.__sequenceIndex === 'number' ? targetDoc.__sequenceIndex : targetGlobalIndex,
            suppressAutoEffect: true,
          });
          return;
        }
      }

      const docs = await fetchData(targetPage, { showPageSpinner: false });
      if (!Array.isArray(docs) || docs.length === 0) return;

      suppressFetchRef.current = true;
      setCurrentPage(targetPage);

      const targetDoc = docs[targetIndexWithinPage];
      if (targetDoc?.id) {
        await handleCardClick(targetDoc.id, { globalIndex: targetGlobalIndex, suppressAutoEffect: true });
      }
    },
    [
      currentDocumentGlobalIndex,
      currentPage,
      detailedData,
      documentMatchesActiveFilters,
      documentSequence,
      fetchData,
      handleCardClick,
      pageLimit,
      totalDocuments,
    ]
  );

  const handlePreviousDocumentNavigation = useCallback(() => {
    handleNavigateDocument(-1);
  }, [handleNavigateDocument]);

  const handleNextDocumentNavigation = useCallback(() => {
    handleNavigateDocument(1);
  }, [handleNavigateDocument]);

  useEffect(() => {
    if (!detailedData) return;
    if (!Array.isArray(sortedFilteredDataList)) return;

    if (pendingFilterSyncRef.current) {
      if (isFetchingPageRef.current) return;

      if (sortedFilteredDataList.length === 0) {
        pendingFilterSyncRef.current = false;
        closeDocumentView({ refetch: false });
        return;
      }

      const firstDoc = sortedFilteredDataList[0];
      if (firstDoc?.id) {
        pendingFilterSyncRef.current = false;
        handleCardClick(firstDoc.id, {
          globalIndex:
            typeof firstDoc.__sequenceIndex === 'number'
              ? firstDoc.__sequenceIndex
              : 0,
        });
      }
      return;
    }

    if (suppressAutoNavigationRef.current) {
      return;
    }

    if (sortedFilteredDataList.length === 0) {
      closeDocumentView({ refetch: false });
      return;
    }

    const stillVisible = sortedFilteredDataList.some((doc) => doc.id === detailedData.id);
    if (!stillVisible) {
      // This list only contains the currently loaded page. During refetches the
      // active document can briefly fall out of the page slice without leaving
      // the broader filtered result set, so do not auto-jump to page row 1.
      return;
    }
  }, [
    sortedFilteredDataList,
    detailedData,
    closeDocumentView,
    handleCardClick,
  ]);

  const documentView = detailedData ? (
    <ReviewPage
      detailedData={detailedData}
      editableData={editableData}
      tempEditableData={tempEditableData}
      sites={locations}
      suppliers={suppliers}
      nominalCodes={nominalCodes}
      taxCodes={taxCodes}
      docTypes={docTypes}
      docStatuses={docStatuses}
      docRejectReasons={docRejectReasons}
      currentImageIndex={currentImageIndex}
      isEdited={isEdited}
      transformWrapperRef={transformWrapperRef}
      handleBackClick={handleBackClick}
      handleFieldChange={handleFieldChange}
      handleFieldBlur={handleFieldBlur}
      handlePreviousImage={handlePreviousImage}
      handleNextImage={handleNextImage}
      handleSaveChanges={handleSaveChanges}
      handleApproveForImport={handleApproveForImport}
      handleRejectDocument={handleRejectDocument}
      otherDocuments={documentSequence}
      handleCardClick={handleCardClick}
      getLocationOptionLabel={getLocationOptionLabel}
      getSupplierOptionLabel={getSupplierOptionLabel}
      getNominalCodeOptionLabel={getNominalCodeOptionLabel}
      getDocTypeOptionLabel={getDocTypeOptionLabel}
      getDocStatusOptionLabel={getDocStatusOptionLabel}
      refetchData={refetchData}
      imageName={getImageName()}
      hasPreviousDocument={hasPreviousDocument}
      hasNextDocument={hasNextDocument}
      onPreviousDocument={handlePreviousDocumentNavigation}
      onNextDocument={handleNextDocumentNavigation}
      documentPosition={
        currentDocumentGlobalIndex >= 0
          ? currentDocumentGlobalIndex + 1
          : 0
      }
      documentCount={totalDocuments}
      totalNetValue={totalNetValue}
      documentLoading={docLoading}
      isReadOnly={isReaderOnly}
      showOtherDocuments={false}
    />
  ) : null;

  return (
    <Container className="fullScreenContainer" maxWidth={false} disableGutters sx={{
      minHeight: '110svh',         // better on mobile than 100vh
      bgcolor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      pb: 6                        // <-- bottom padding (theme * 8px)
    }}
    >
      {notification.open && (
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ top: 16, right: 16, left: 'auto' }}
        >
          <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.type} sx={{ minWidth: 240 }}>
            {notification.message}
          </Alert>
        </Snackbar>
      )}
      <GridView
        locations={locations}
        suppliers={suppliers}
        filteredDataList={sortedFilteredDataList}
        handleCardClick={handleCardClick}
        selectedSupplier={selectedSupplier}
        selectedLocation={selectedLocation}
        selectedRepairLocation={selectedRepairLocation}
        selectedNominalCode={selectedNominalCode}
        handleSupplierChange={handleSupplierChange}
        handleLocationChange={handleLocationChange}
        handleRepairLocationChange={handleRepairLocationChange}
        handleNominalCodeChange={handleNominalCodeChange}
        handleClearFilters={handleClearFilters}
        supplierStartChar={supplierStartChar}
        supplierEndChar={supplierEndChar}
        onSupplierStartCharChange={handleSupplierStartCharChange}
        onSupplierEndCharChange={handleSupplierEndCharChange}
        sortCriteria={sortCriteria}
        setSortCriteria={setSortCriteria}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        totalDocuments={totalDocuments}
        totalNetValue={totalNetValue}
        pageLimit={pageLimit}
        selectedDocStatuses={selectedDocStatuses}
        selectedDocTypes={selectedDocTypes}
        handleDocStatusChange={handleDocStatusChange}
        handleDocTypeChange={handleDocTypeChange}
        docStatuses={docStatuses}
        docTypes={docTypes}
        nominalCodes={nominalCodes}
        docSearchQuery={docSearchQuery}
        onSearchDocument={handleSearchDocument}
        uploadedStartDate={uploadedStartDate}
        uploadedEndDate={uploadedEndDate}
        onUploadedStartDateChange={handleUploadedStartDateChange}
        onUploadedEndDateChange={handleUploadedEndDateChange}
        createdStartDate={createdStartDate}
        createdEndDate={createdEndDate}
        onCreatedStartDateChange={handleCreatedStartDateChange}
        onCreatedEndDateChange={handleCreatedEndDateChange}
        discountOperator={discountOperator}
        discountValue={discountValue}
        onDiscountOperatorChange={handleDiscountOperatorChange}
        onDiscountValueChange={handleDiscountValueChange}
        includeInNetTotalsFilter={includeInNetTotalsFilter}
        creditRequestedFilter={creditRequestedFilter}
        onIncludeInNetTotalsFilterChange={handleIncludeInNetTotalsFilterChange}
        onCreditRequestedFilterChange={handleCreditRequestedFilterChange}
        invoiceStartDate={invoiceStartDate}
        invoiceEndDate={invoiceEndDate}
        onInvoiceStartDateChange={handleInvoiceStartDateChange}
        onInvoiceEndDateChange={handleInvoiceEndDateChange}
        jobNumberFilter={jobNumberFilter}
        onJobNumberSearch={handleSearchJobNumber}
        onExportCsv={handleExportCsv}
        exporting={exporting}
        loading={pageLoading || docLoading}
        defaultSelectedDocStatuses={defaultSelectedDocStatuses}
        defaultSelectedDocTypes={defaultSelectedDocTypes}
        defaultSortCriteria=""
        defaultSortOrder="asc"
        documentView={documentView}
      />
    </Container>
  );
};

export default ReviewDocsPage;
